import { useTranslation } from "react-i18next";
import useRelatedShows, {
  RECOMMENDATIONS_RAIL_SIZE,
} from "../hooks/useRelatedShows";
import RecommendedShowCard from "./RecommendedShowCard";
import SectionHeader from "./SectionHeader";
import SeriesRail from "./SeriesRail";
import type { SeriesDetailsShow } from "../hooks/useSeriesDetails";
import { useTracking } from "@/components/ClientAnalytics";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";
import { CoverSize } from "@/codegen/graphql/generated/graphql";
import { Skeleton } from "@/components/ui/skeleton";
import { thumbnailHeights } from "@/modules/CatalogLayout/components/covers/DefaultCover";

const RAIL_ITEMS = "gap-3 *:w-28 md:gap-4 md:*:w-32";

/**
 * "We think you might love" (SHO-757): a rail of the first recommended
 * series, View all to the full list when there are more than the rail holds.
 * Its skeleton while its query runs — the page's skeleton draws the rail, so
 * it must not vanish and come back — nothing when the API has none: the
 * ticket wants no empty gap.
 */
const RecommendationsSection: React.FC<{
  show: SeriesDetailsShow;
  series: string;
}> = ({ show, series }) => {
  const { t } = useTranslation();
  const { trackAmplitudeEvent } = useTracking();
  const shows = useRelatedShows(show.id);

  if (shows == null) return <RecommendationsSectionSkeleton />;
  if (shows.length === 0) return null;

  const viewAll = {
    to: "/series/$series/recommendations" as const,
    series,
    testId: "series-recommendations-view-all",
    onClick: () =>
      trackAmplitudeEvent(
        AmplitudeTrackingEvent.openSeriesDetailsRecommendations,
        { "series name": show.title },
      ),
  };

  return (
    <section
      data-test-id="series-recommendations-section"
      className="flex flex-col gap-3"
    >
      <SectionHeader
        title={t("series_details_recommendations_title_web")}
        viewAll={shows.length > RECOMMENDATIONS_RAIL_SIZE ? viewAll : undefined}
      />
      <SeriesRail itemClassName={RAIL_ITEMS}>
        {shows.slice(0, RECOMMENDATIONS_RAIL_SIZE).map((s) => (
          <RecommendedShowCard
            key={s.id}
            show={s}
            origin="series details recommendations"
            thumbnailHeight={thumbnailHeights[CoverSize.Small]}
          />
        ))}
      </SeriesRail>
    </section>
  );
};

/** The section's shape, also what the page's skeleton draws for it. */
const RecommendationsSectionSkeleton = () => (
  <section className="flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <Skeleton className="h-5.5 w-56" />
      <Skeleton className="h-4 w-16" />
    </div>
    <SeriesRail static itemClassName={RAIL_ITEMS}>
      {Array.from({ length: 7 }, (_, i) => (
        <RecommendedShowCard.Skeleton key={i} />
      ))}
    </SeriesRail>
  </section>
);

export default Object.assign(RecommendationsSection, {
  Skeleton: RecommendationsSectionSkeleton,
});
